/**
 * Quick View Cart Modal
 * =====================
 * Custom element (<quick-view-modal>) that renders a product quick-view overlay
 * without leaving the current page. Supports:
 *
 *  - Collection page quick-buy buttons  (a[data-button-quick-view][data-handle])
 *  - Colour swatch links inside the modal (a[data-swatch][product-handle])
 *  - ATS (Available-to-Ship) inventory for Pro members via geostock.app
 *  - Flickity image carousel with reliable init/destroy lifecycle
 *  - Hover prefetch: product HTML is fetched the moment a user hovers a button
 *    or swatch, so by the time they click the response is already cached
 *
 * Performance notes:
 *  - Images are requested at 311px wide (the modal gallery width) with srcset
 *    candidates at 311 / 622 (2×) / 720 (3×) only — see snippets/media.liquid
 *  - The prefetch cache is a Map keyed by product handle. Entries are promises
 *    so concurrent hovers on the same product don't double-fetch.
 *
 * Build: this file is concatenated and minified into themePackage.min.js
 *        via `npm run minify-js`. Always edit this source, never the .min file.
 */

// ---------------------------------------------------------------------------
// Gallery loading overlay CSS
// Injected at runtime so it travels with this JS file and doesn't require a
// separate stylesheet entry. Applied via the class `qv-gallery--loading`.
//
// Behaviour:
//   • Gallery children become invisible (visibility:hidden) — they still occupy
//     their natural space so the container doesn't collapse/jump in height.
//   • A CSS spinner (::before pseudo-element) is centered over the gallery.
//   • max-height: 400px prevents an oversized loading state on tall products.
// ---------------------------------------------------------------------------
(function() {
  const s = document.createElement('style');
  s.textContent = `
    .popup-quick-view__gallery.qv-gallery--loading { position: relative; max-height: 400px; overflow: hidden; }
    .popup-quick-view__gallery.qv-gallery--loading > * { visibility: hidden; }
    .popup-quick-view__gallery.qv-gallery--loading::before {
      content: ''; position: absolute; top: 50%; left: 50%;
      transform: translate(-50%, -50%);
      width: 30px; height: 30px;
      border: 2px solid rgba(0,0,0,0.15); border-top-color: #333; border-radius: 50%;
      z-index: 6; animation: qv-spin 0.65s linear infinite;
    }
    @keyframes qv-spin { to { transform: translate(-50%, -50%) rotate(360deg); } }
  `;
  document.head.appendChild(s);
})();

class QuickViewModal extends HTMLElement {
  constructor() {
    super();

    this.isOpen = false;
    this.productHandle = "";

    // Kept for reference; actual click handling uses event delegation (see assignEventButtons)
    this.quickBuyButtons = document.querySelectorAll(
      "a[data-button-quick-view]"
    );

    this.assignEventButtons();
    this.render();
    this.addEventListeners();
  }

  // ---------------------------------------------------------------------------
  // assignEventButtons()
  // Sets up two delegated listeners on `document`:
  //
  //  1. mouseover — triggers prefetch() for both quick-buy buttons and swatches
  //     inside the modal. This starts fetching product HTML before the user
  //     clicks, so the modal can open near-instantly.
  //
  //  2. click — opens the modal for quick-buy buttons. Uses event delegation
  //     (closest()) rather than binding to individual buttons so it works with
  //     dynamically rendered cards (e.g. SearchSpring results, infinite scroll).
  // ---------------------------------------------------------------------------
  assignEventButtons() {
    // CAPTURE phase — fires before any element-level listener (e.g. product tracking scripts
    // that call stopPropagation and block our bubbling listener).
    document.addEventListener("click", (event) => {
      const btn = event.target.closest("[data-button-quick-view]");
      if (!btn) return;

      const handle = btn.getAttribute("data-handle");
      if (!handle) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      this.open(handle);
    }, true);

    // Prefetch on hover so the response is cached by the time the user clicks
    document.addEventListener("mouseover", (event) => {
      const button = event.target.closest("a[data-button-quick-view]");
      if (button) {
        const handle = button.getAttribute("data-handle");
        if (handle) this.prefetch(handle);
        return;
      }
      // Also prefetch when hovering colour swatches inside an open modal
      const swatch = event.target.closest("a[data-swatch][product-handle]");
      if (swatch) {
        const handle = swatch.getAttribute("product-handle");
        if (handle) this.prefetch(handle);
      }
    });

    document.addEventListener("click", (event) => {
      const button = event.target.closest("a[data-button-quick-view]");
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      const handle = button.getAttribute("data-handle");
      this.open(handle);
    });
  }

  // ---------------------------------------------------------------------------
  // prefetch(handle)
  // Fires a background fetch for the product's quick-view HTML and caches the
  // resulting promise in this._prefetchCache (Map keyed by handle).
  //
  // Rules:
  //  • Only one in-flight request per handle — subsequent calls for the same
  //    handle while the first is still pending are no-ops.
  //  • On network error the cache entry is deleted so the next click can retry.
  //  • loadProductContent() awaits the cached promise directly, so if the fetch
  //    already resolved the modal renders with zero additional network wait.
  // ---------------------------------------------------------------------------
  prefetch(handle) {
    if (!handle) return;
    if (!this._prefetchCache) this._prefetchCache = new Map();
    if (this._prefetchCache.has(handle)) return; // already in flight or done
    const promise = fetch(`/products/${handle}?section_id=api-quickview`)
      .then(r => r.ok ? r.text() : Promise.reject(r.status))
      .catch(() => { this._prefetchCache.delete(handle); }); // remove on error so retry is possible
    this._prefetchCache.set(handle, promise);
  }

  // ---------------------------------------------------------------------------
  // render()
  // Writes the modal shell HTML into the custom element. Called once on init.
  // The inner .quick-view-modal-wrapper is replaced with product HTML on open.
  // ---------------------------------------------------------------------------
  render() {
    this.innerHTML = `
      <div class="quick-view-overlay" id="quick-view-overlay">
        <div class="quick-view-modal-wrapper">
            <button class="quick-view-close-button" id="quick-view-close-button">&times;</button>
            <div class="quick-view-modal-content" id="quick-view-modal-content">
                <div class="quick-view-loading"></div>
            </div>
        </div>
      </div>
    `;
  }

  // ---------------------------------------------------------------------------
  // addEventListeners()
  // Wires up close behaviours: ✕ button, backdrop click, Escape key.
  // Also handles legacy [handle] attribute triggers from the theme.
  // ---------------------------------------------------------------------------
  addEventListeners() {
    const closeButton = this.querySelector("#quick-view-close-button");
    closeButton.addEventListener("click", () => this.close());

    // Close when clicking the darkened backdrop (not the modal card itself)
    const overlay = this.querySelector("#quick-view-overlay");
    overlay.addEventListener("click", (event) => {
      if (event.target === overlay) {
        this.close();
      }
    });

    document.addEventListener("keydown", (event) => {
      if (event.key === "Escape" && this.isOpen) {
        this.close();
      }
    });

    // Legacy theme trigger: elements with a bare [handle] attribute
    document.addEventListener("click", (event) => {
      const button = event.target.closest("[handle]");
      if (button) {
        const handle = button.getAttribute("handle");
        if (handle) {
          event.preventDefault();
          this.open(handle);
        }
      }
    });
  }

  // ---------------------------------------------------------------------------
  // open(handle)
  // Entry point for opening the modal. Shows the overlay immediately, locks
  // body scroll, then delegates to loadProductContent() to fetch and render.
  // ---------------------------------------------------------------------------
  open(handle) {
    if (!handle) return;

    this.productHandle = handle;
    this.isOpen = true;

    const overlay = this.querySelector("#quick-view-overlay");
    overlay.classList.add("active");

    document.body.style.overflow = "hidden";

    this.loadProductContent();
    this.onModalOpened();
  }

  openWithEvent(event) {
    const parent = event.target.closest("[data-button-quick-view]");
    if (!parent) return;

    const handle = parent.getAttribute("data-handle");
    this.open(handle);
  }

  // ---------------------------------------------------------------------------
  // destroyFlickity()
  // Safely tears down the Flickity carousel instance and nulls the reference.
  // Must be called before replacing modal HTML to prevent orphaned resize/drag
  // event listeners accumulating on window with each product load.
  // ---------------------------------------------------------------------------
  destroyFlickity() {
    if (this.flickity) {
      try { this.flickity.destroy(); } catch(e) {}
      this.flickity = null;
    }
  }

  // ---------------------------------------------------------------------------
  // close()
  // Hides the overlay, destroys the carousel, restores body scroll, and fires
  // the theme:quick-view:close event for any theme integrations listening.
  // ---------------------------------------------------------------------------
  close() {
    this.isOpen = false;
    this.destroyFlickity();
    const overlay = this.querySelector("#quick-view-overlay");
    overlay && overlay.classList.remove("active");
    document.body.style.overflow = "";
    document.dispatchEvent(
      new CustomEvent("theme:quick-view:close", {
        detail: {},
        bubbles: true,
      })
    );
  }

  // ---------------------------------------------------------------------------
  // loadProductContent()
  // Core async method. Fetches product HTML from the api-quickview section,
  // injects it into the modal, then sets up functionality and the carousel.
  //
  // Race condition protection — generation counter (_loadGen):
  //   Every call increments _loadGen and captures the value as `gen`. Before
  //   any DOM mutation after an await, the method checks `gen === this._loadGen`.
  //   If a newer call has started (e.g. the user clicked a different swatch
  //   before the first fetch resolved) the stale callback returns early without
  //   touching the DOM, preventing flicker or incorrect content being shown.
  //
  // Loading states:
  //   • First open (no existing gallery): replaces modal content with a spinner.
  //   • Swatch change (gallery already present): keeps existing content visible
  //     and applies qv-gallery--loading only to the gallery div, so the product
  //     title, form, and swatches remain readable during the fetch.
  //
  // ATS (Available-to-Ship) for Pro members:
  //   When window.isPro === true the method also calls the geostock.app API to
  //   get real-time stock counts per variant. Sold-out radio buttons are marked
  //   with .sold-out and the ATS map is cached in this.atsInventoryMap so that
  //   updateSelectedVariant() can use it without re-fetching.
  // ---------------------------------------------------------------------------
  async loadProductContent() {
    // Increment generation counter so stale callbacks can self-abort
    this._loadGen = (this._loadGen || 0) + 1;
    const gen = this._loadGen;

    // Destroy Flickity before replacing the DOM so window listeners are cleaned up
    this.destroyFlickity();

    const modalContent = this.querySelector(".quick-view-modal-wrapper");
    try {
      // If we already have product content (swatch change), keep the modal visible
      // and only apply a loading overlay to the gallery — much faster perceived experience.
      // On first open, show the full loading spinner.
      const existingGallery = modalContent.querySelector('.popup-quick-view__gallery');
      if (existingGallery) {
        existingGallery.classList.add('qv-gallery--loading');
      } else {
        modalContent.innerHTML = '<div class="quick-view-loading"></div>';
      }

      // Use prefetched response if available, otherwise fetch now.
      // prefetch() fires on hover, so by click time the promise is usually already resolved.
      const cached = this._prefetchCache?.get(this.productHandle);
      const html = await (cached || fetch(`/products/${this.productHandle}?section_id=api-quickview`).then(r => {
        if (!r.ok) throw new Error(`HTTP error! status: ${r.status}`);
        return r.text();
      }));

      // Bail if a newer load already took over
      if (gen !== this._loadGen) return;

      modalContent.innerHTML = html;

      // Apply gallery loading overlay immediately after DOM insertion so the
      // browser never paints un-initialised stacked Flickity slides
      const gallery = modalContent.querySelector('.popup-quick-view__gallery');
      if (gallery) gallery.classList.add('qv-gallery--loading');

      this.lastClickedHandle = this.productHandle;

      // Mark the matching colour swatch as active
      const swatchToActivate = this.querySelector(
        `a[data-swatch][product-handle="${this.lastClickedHandle}"]`
      );
      if (swatchToActivate) {
        swatchToActivate.classList.add("is-active");
      }

      if (typeof Drawer === 'function') {
        new Drawer(document);
      }

      const productJsonEl = modalContent.querySelector('[data-product-json]');
      if (!productJsonEl) {
        console.error('Quick view: Missing product JSON');
        return;
      }

      let productData;
      try {
        productData = JSON.parse(productJsonEl.textContent);
      } catch (e) {
        console.error('Quick View: Invalid product JSON format', e);
        return;
      }

      // If we already have an ATS map from a previous load (same session),
      // pre-apply availability before the fresh ATS fetch resolves
      if (this.atsInventoryMap) {
        productData.variants.forEach((variant) => {
          const atsQty = this.atsInventoryMap[variant.id];
          if (typeof atsQty === 'number') {
            variant.available = atsQty > 0;
          }
        });
        productJsonEl.textContent = JSON.stringify(productData);
      }

      const variantIds = productData.variants.map((v) => v.id).join(',');

      if (window.isPro === true || document.body.getAttribute('data-pro') === 'true') {
        // ---------------------------------------------------------------------------
        // ATS inventory fetch (Pro members only)
        // Calls geostock.app to get per-variant stock at the "ATS" location.
        // Results are stored in this.atsInventoryMap so updateSelectedVariant()
        // can reference them without re-fetching on every radio change.
        // ---------------------------------------------------------------------------
        fetch(`https://api.geostock.app/shop/toadandco.myshopify.com/multi_variant_locations/${variantIds}`)
          .then((res) => res.json())
          .then((atsData) => {
            this.atsInventoryMap = {};

            productData.variants.forEach((variant) => {
              const ats = atsData[variant.id]?.locations?.find(loc => loc.name === "ATS");
              const stockQty = ats?.stock ?? 0;
              this.atsInventoryMap[variant.id] = stockQty;

              const inputSelectors = modalContent.querySelectorAll('[data-single-option-selector]');

              if (!inputSelectors.length) {
                console.warn('[QuickViewModal] No variant inputs found.');
                return;
              }

              // Mark each radio button as sold-out based on ATS stock
              inputSelectors.forEach((input) => {
                const indexAttr = input.dataset.index;
                if (!indexAttr || !indexAttr.includes('option')) return;

                const index = parseInt(indexAttr.replace('option', ''), 10) - 1;
                const value = input.value;

                if (variant.options && variant.options[index] === value) {
                  const radioButton = input.closest('.radio__button');
                  if (radioButton) {
                    if (stockQty <= 0) {
                      radioButton.classList.add('sold-out');
                    } else {
                      radioButton.classList.remove('sold-out');
                    }
                  }
                }
              });
            });

          })
          .then(() => {
            // After ATS data is applied, init product JS then carousel.
            // Double rAF ensures the browser has painted the new DOM before
            // Flickity measures cell widths.
            if (gen !== this._loadGen) return;
            this.initProductFunctionality();
            const g = gen, gal = modalContent.querySelector('.popup-quick-view__gallery');
            requestAnimationFrame(() => {
              requestAnimationFrame(() => { if (g === this._loadGen) this.initFlickity(gal); });
            });
          })
          .catch((error) => {
            console.error("Error fetching ATS stock for quick view:", error);
            if (gen !== this._loadGen) return;
            this.initProductFunctionality();
            const g = gen, gal = modalContent.querySelector('.popup-quick-view__gallery');
            requestAnimationFrame(() => {
              requestAnimationFrame(() => { if (g === this._loadGen) this.initFlickity(gal); });
            });
          });
      } else {
        // Non-Pro path: skip ATS fetch, go straight to init
        this.initProductFunctionality();
        const g = gen, gal = modalContent.querySelector('.popup-quick-view__gallery');
        requestAnimationFrame(() => {
          requestAnimationFrame(() => { if (g === this._loadGen) this.initFlickity(gal); });
        });
      }

    } catch (error) {
      console.error("Error loading product content:", error);
      modalContent.innerHTML = `<div style="text-align: center; padding: 20px;">
        <p>Sorry, we couldn't load the product content.</p>
        <p>Please try again later.</p>
      </div>`;
    }
  }

  // ---------------------------------------------------------------------------
  // onModalOpened()
  // Reads the active swatch value and updates the colour name label
  // (#quickview-modal-color-name) in the modal header. Delayed 800ms to allow
  // the async product HTML to finish rendering before querying the DOM.
  // ---------------------------------------------------------------------------
  onModalOpened() {
    setTimeout(() => {
      const container = document.querySelector('.popup-quick-view__body');
      if (!container) return;

      const fieldset = container.querySelector('fieldset.siblings');
      const activeSwatch = fieldset?.querySelector('a.is-active');
      const swatchValue = activeSwatch?.dataset?.swatch;

      const label = container.querySelector('#quickview-modal-color-name');

      if (label && swatchValue) {
        label.textContent = swatchValue;
      }

    }, 800);
  }

  resetContent() {
    const modalContent = this.querySelector("#quick-view-modal-content");
    if (modalContent) {
      modalContent.innerHTML = '<div class="quick-view-loading"></div>';
    }
    this.productHandle = "";
  }

  // ---------------------------------------------------------------------------
  // initProductFunctionality()
  // Called once the product HTML is in the DOM. Wires up all interactive
  // elements inside the modal: close button, swatches, variant inputs,
  // add-to-cart, price display, and size chart drawer.
  // ---------------------------------------------------------------------------
  initProductFunctionality() {
    const closeButton = this.querySelector(".popup-quick-view__close");
    closeButton?.addEventListener("click", (e) => {
      e.preventDefault();
      this.close();
    });

    this.initSwatches();
    this.addEventsToInputs();
    this.assignSubmitEventToAddToCart();
    this.checkPrices();
    this.updateSelectedVariant();
    this.addSizeChartToggleListener();
  }

  // ---------------------------------------------------------------------------
  // _calcPricingProperties(variantId)
  // Mirrors the PDP addToCart() pricing logic in custom.js: reads the product
  // JSON already in the modal DOM, finds the variant, then computes _msrp,
  // _net using calculatePriceFromTags (if present) so Pro pricing
  // is reflected correctly. Returns a plain object ready to append to FormData
  // or embed in a JSON cart payload.
  // ---------------------------------------------------------------------------
  _calcPricingProperties(variantId) {
    const productJsonEl = this.querySelector('[data-product-json]');
    if (!productJsonEl) return {};
    let product;
    try { product = JSON.parse(productJsonEl.textContent); } catch(e) { return {}; }

    const variant = product.variants.find(v => v.id === parseInt(variantId, 10));
    if (!variant) return {};

    const msrp = variant.compare_at_price > 0 ? variant.compare_at_price : variant.price;
    const tags = product.tags || [];
    let net = variant.price;
    if (window.calculatePriceFromTags) {
      const result = window.calculatePriceFromTags(variant.price, tags, {}, msrp);
      net = Math.min(result.calculatedPrice, variant.price);
    }
    const fmt = cents => (cents / 100).toFixed(2);

    const props = {
      '_msrp': fmt(msrp),
      '_net': fmt(net),
    };
    return props;
  }

  _appendPricingProperties(formData) {
    const props = this._calcPricingProperties(formData.get('id'));
    for (const [key, value] of Object.entries(props)) {
      formData.append(`properties[${key}]`, value);
    }
  }

  // ---------------------------------------------------------------------------
  // assignSubmitEventToAddToCart()
  // Delegates click and submit events for the add-to-cart form. Bound once per
  // modal lifetime (__delegatedSubmitHandlerBound guard) so re-rendering product
  // HTML on swatch changes doesn't stack duplicate listeners.
  //
  // Click path: intercepts .btn--submit clicks for AJAX add-to-cart, then opens
  //   the cart drawer. Falls back to TC_openCartDrawer → cartDrawer chain.
  //
  // Submit path: intercepts native form submit, hands off to cartDrawer.addToCart
  //   if available, otherwise falls back to a direct /cart/add.js fetch.
  //
  // Click lock: a 800ms data-notifyClickLock flag prevents double-firing from
  //   rapid successive clicks.
  // ---------------------------------------------------------------------------
  assignSubmitEventToAddToCart() {
    // Bind ONCE per modal lifetime
    if (this.__delegatedSubmitHandlerBound) return;
    this.__delegatedSubmitHandlerBound = true;

    this.addEventListener('click', (e) => {
      const btn = e.target.closest('.btn--submit');
      if (!btn) return;

      const form = this.querySelector('form[action="/cart/add"]');
      if (!form) return;

      // CLICK LOCK — prevents double-submit on rapid clicks
      if (btn.dataset.notifyClickLock === 'true') return;
      btn.dataset.notifyClickLock = 'true';
      setTimeout(() => {
        btn.dataset.notifyClickLock = 'false';
      }, 800);

      const hasNotificationPopup = btn.hasAttribute('data-bis-notification-popup');

      // SOLD OUT / NOTIFY ME — let the BIS popup handle it, don't add to cart
      if (hasNotificationPopup) {
        e.preventDefault();
        e.stopPropagation();

        return;
      }

      // AJAX Add to Cart
      e.preventDefault();
      e.stopPropagation();

      const formData = new FormData(form);
      this._appendPricingProperties(formData);
      btn.classList.add('loading');
      btn.disabled = true;

      fetch('/cart/add.js', {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
        },
        body: formData
      })
        .then(response => response.json())
        .then(data => {
          btn.classList.remove('loading');
          btn.disabled = false;
          // Always open the cart drawer modal after successful add
          if (typeof window.TC_openCartDrawer === 'function') {
            window.TC_openCartDrawer();
            this.close();
          } else if (window.cart && typeof window.cart.refreshDrawer === 'function') {
            window.cart.refreshDrawer();
            this.close();
          }
        })
        .catch(error => {
          btn.classList.remove('loading');
          btn.disabled = false;
          alert('Could not add to cart. Please try again.');
        });
    });

    // Intercept form submit to use cart drawer (avoid redirect to cart page)
    this.addEventListener('submit', (e) => {
      const form = e.target.closest('form[action="/cart/add"]');
      if (!form) return;
      e.preventDefault();
      e.stopPropagation();

      const submitBtn = form.querySelector('.btn--submit');
      if (submitBtn?.hasAttribute('data-notification-popup')) {
        return;
      }

      const formData = new FormData(form);
      this._appendPricingProperties(formData);

      if (window.cart && typeof window.cart.addToCart === 'function') {
        window.cart.button = submitBtn;
        window.cart.form = form;
        // Ensure form wrapper is set for loading/error states
        window.cart.formWrapper = submitBtn?.closest('[data-form-wrapper]') || submitBtn?.closest('form');

        // If variant id is missing, abort
        if (!formData.get('id')) {
          console.warn('[QuickView] Missing variant id for add to cart');
          return;
        }

        window.cart.addToCart(formData);
        // Open the drawer after a brief delay; getCart() is already called internally by addToCart on success
        setTimeout(() => {
          window.cart.openCartDrawer?.();
        }, 250);

        this.close();
        return;
      }

      // Fallback: AJAX add + open drawer if available
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: formData.get('id'), quantity: formData.get('quantity') || 1, properties: this._calcPricingProperties(formData.get('id')) })
      })
        .then(() => {
          if (window.cart?.refreshDrawer) window.cart.refreshDrawer();
          this.close();
        })
        .catch((err) => console.error('[QuickView] add to cart failed', err));
    });
  }

  // ---------------------------------------------------------------------------
  // checkPrices()
  // Hides the compare-at price element when it matches the sale price, which
  // can happen when a discount code is applied and both values resolve equally.
  // ---------------------------------------------------------------------------
  checkPrices() {
    const priceWrapper = this.querySelector('.product__price[data-price-wrapper]');
    if (!priceWrapper) return;
    const regularPriceEl = priceWrapper.querySelector('[data-product-price]');
    const comparePriceEl = priceWrapper.querySelector('[data-compare-price]');
    if (regularPriceEl && comparePriceEl && regularPriceEl.textContent.trim() === comparePriceEl.textContent.trim()) {
      comparePriceEl.classList.add('hidden');
    }
  }

  // ---------------------------------------------------------------------------
  // addEventsToInputs()
  // Binds change listeners to all variant selector inputs (radio, checkbox,
  // select) so that updateSelectedVariant() fires whenever the user picks a
  // different size, colour, etc.
  // ---------------------------------------------------------------------------
  addEventsToInputs() {
    const inputs = this.querySelectorAll("input[type='radio'], input[type='checkbox'], select");
    inputs.forEach((input) => {
      input.addEventListener("change", () => this.updateSelectedVariant());
    });
  }

  // ---------------------------------------------------------------------------
  // updateSelectedVariant()
  // Reads the currently selected option values from the form, finds the
  // matching variant in the product JSON, then:
  //   1. Updates the hidden `id` input so the form posts the correct variant
  //   2. Fires the theme:quick-view-variant:change custom event
  //   3. Applies ATS availability override for Pro users
  //   4. Updates the Add to Cart / Sold Out button state and label
  //   5. Adds/removes variant--soldout class on the item wrapper
  //   6. Calls markSoldOutVariants() to cross-out unavailable option labels
  // ---------------------------------------------------------------------------
  updateSelectedVariant() {
    const form = this.querySelector('form[action="/cart/add"]');
    if (!form) return;

    const options = [];
    form.querySelectorAll('[data-single-option-selector]').forEach((el) => {
      const value = el.type === 'radio' ? (el.checked ? el.value : null) : el.value;
      if (value !== null) options.push(value);
    });

    const productJson = this.querySelector('[data-product-json]');
    if (!productJson) return;
    const product = JSON.parse(productJson.textContent);

    const matchedVariant = product.variants.find((variant) =>
      variant.options.every((opt, idx) => opt === options[idx])
    );
    if (!matchedVariant) return;

    // Update ShopifyPrice element with the selected variant's pricing (PDP path)
    const shopifyPriceEl = this.querySelector('shopify-price');
    if (shopifyPriceEl && typeof shopifyPriceEl.updateState === 'function') {
      const priceScriptEl = this.querySelector('[shopify-price-json]');
      let priceData = null;
      if (priceScriptEl) {
        try { priceData = JSON.parse(priceScriptEl.textContent); } catch(e) {}
      }
      if (priceData) {
        const msrp = matchedVariant.compare_at_price > 0 ? matchedVariant.compare_at_price : matchedVariant.price;
        let priceAfterDiscounts = matchedVariant.price;
        if (window.calculatePriceFromTags) {
          const tags = (priceData.product && priceData.product.tags) || [];
          const result = window.calculatePriceFromTags(matchedVariant.price, tags, {}, msrp);
          priceAfterDiscounts = result.calculatedPrice;
        }
        console.log('[QV] updateState call — variant.price:', matchedVariant.price, '| variant.compare_at_price:', matchedVariant.compare_at_price, '| product.compare_at_price:', priceData.product?.compare_at_price, '| priceAfterDiscounts:', priceAfterDiscounts);
        shopifyPriceEl.updateState({
          currentVariant: matchedVariant,
          product: priceData.product,
          priceAfterDiscounts,
        });
      }
    }

    // Update Liquid-rendered quickview price spans (quickview path — no shopify-price element)
    const priceWrapper = this.querySelector('.product__price[data-price-wrapper]');
    if (priceWrapper && !shopifyPriceEl) {
      const basePrice = matchedVariant.price || 0;
      const variantCompare = matchedVariant.compare_at_price || 0;
      const productCompare = product.compare_at_price || 0;
      const tags = Array.isArray(product.tags) ? product.tags : (product.tags || '').split(',').map(t => t.trim());
      const isProExcluded = tags.some(tag => typeof tag === 'string' && tag.trim() === 'ProExclusion');

      let finalPrice = basePrice;
      if (window.isPro === true && !isProExcluded) {
        const proSetTag = tags.find(tag => typeof tag === 'string' && tag.trim().startsWith('ProSetPrice'));
        if (proSetTag) {
          const setPrice = parseFloat(proSetTag.replace('ProSetPrice', '')) * 100;
          if (setPrice > 0) finalPrice = setPrice;
        } else {
          finalPrice = variantCompare * (100 - (window.proDiscount || 0)) / 100;
        }
      }

      const comparePrice = variantCompare > finalPrice ? variantCompare
        : productCompare > finalPrice ? productCompare
        : 0;

      console.log('[QV] DOM price update — basePrice:', basePrice, '| finalPrice:', finalPrice, '| comparePrice:', comparePrice);

      if (comparePrice > 0) {
        priceWrapper.innerHTML = `
          <span class="discounted-price product-grid-item__price-new price" aria-label="sale price">${Shopify.formatMoney(finalPrice)}</span>
          <span class="original-price product-grid-item__price-new price" aria-label="original price" style="text-decoration: line-through;">${Shopify.formatMoney(comparePrice)}</span>
        `;
      } else {
        priceWrapper.innerHTML = `<span class="price">${Shopify.formatMoney(finalPrice)}</span>`;
      }
    }

    const atcButton = form.querySelector('.btn--submit');
    const ctaTextEl = this.querySelector('[data-add-to-cart-text]');

    if (!atcButton || !ctaTextEl) return;

    const showBISModal = atcButton.hasAttribute('data-show-bis');

    const idInput = form.querySelector('input[name="id"]');
    if (idInput) idInput.value = matchedVariant.id;

    document.dispatchEvent(
      new CustomEvent("theme:quick-view-variant:change", {
        detail: {
          variant: matchedVariant,
        },
        bubbles: true,
      })
    );

    // Apply ATS availability override for Pro users
    if ((window.isPro || document.body.getAttribute('data-pro') === 'true') && this.atsInventoryMap) {
      const atsStock = this.atsInventoryMap[matchedVariant.id];
      if (typeof atsStock === 'number') {
        matchedVariant.available = atsStock > 0;
      }
    }

    if (matchedVariant.available) {
      ctaTextEl.textContent = 'Add to Cart';
      atcButton.disabled = false;
      atcButton.type = 'submit';
      atcButton.classList.remove('btn--primary', 'btn--solid', 'open-modal-button');

      atcButton.innerHTML = `
        <span data-add-to-cart-text="">Add to Cart</span>
        <em class="loader loader--line"><em class="loader-indeterminate"></em></em>
      `;

      if (showBISModal) {
        atcButton.removeAttribute('data-bis-notification-popup');
      }
    } else {
      ctaTextEl.textContent = 'Sold Out - Notify Me';
      atcButton.disabled = false;
      atcButton.type = 'button';
      atcButton.classList.add('btn--primary', 'btn--solid', 'open-modal-button');
      atcButton.innerHTML = `
        <span data-add-to-cart-text="">Sold Out - Notify Me</span>
        <em class="loader loader--line"><em class="loader-indeterminate"></em></em>
      `;
      if (showBISModal) {
        atcButton.setAttribute('data-bis-notification-popup', true);
      }

      // Re-query after innerHTML replacement
      this.submitButton = this.querySelector('.btn--submit');
    }

    const quickViewItem = this.querySelector('.popup-quick-view__item');
    if (quickViewItem) {
      if (!matchedVariant.available) {
        quickViewItem.classList.add('variant--soldout');
      } else {
        quickViewItem.classList.remove('variant--soldout');
      }
    }

    this.markSoldOutVariants(product);
  }

  buildNotificationMarkup(variant) {
    const productTitle = this.querySelector('.product__title')?.textContent || 'Product';
    const optionTitle = variant.title || '';
    return `
      <div class='product__notification' data-notification>
        <h2 class='product__notification__title'>${productTitle}</h2>
        <p class='product__notification__subtitle'>${optionTitle}</p>
        <form method='post' action='/contact#NotificationForm-${variant.id}' id='NotificationForm-${variant.id}' accept-charset='UTF-8' class='contact-form' data-notification-form>
          <input type='hidden' name='form_type' value='customer' />
          <input type='hidden' name='utf8' value='✓' />
          <div class='product__notification__message'>
            <p>Your email has been received.</p>
          </div>
          <div class='product__notification__form'>
            <label for='Email-${variant.id}'>Please enter your email and we will let you know when your selected item becomes available.</label>
            <input type='hidden' name='contact[tags]' value='newsletter, out-of-stock-notification--${productTitle}'/>
            <input type='hidden' name='contact[first_name]' value='Newsletter' />
            <input type='hidden' name='contact[last_name]' value='Subscriber' />
            <div class='input-group input-group--inline'>
              <div class='input-group__item'>
                <input type='email' placeholder='Your email' name='contact[email]' id='Email-${variant.id}' class='input-group__field input-group__field--email' aria-label='Your email' autocorrect='off' autocapitalize='off' required data-popup-input>
              </div>
              <div class='input-group__item--btn'>
                <button type='submit' name='subscribe' id='SubscribeSubmit-${variant.id}' class='btn btn--primary btn--solid'>
                  <span data-notification-button-text>Notify Me</span>
                </button>
              </div>
            </div>
          </div>
        </form>
        <button title='Close (Esc)' class='product__notification__close' data-popup-close>
          <svg aria-hidden='true' focusable='false' role='presentation' class='icon icon-close' viewBox='0 0 192 192'><path d='M150 42 42 150M150 150 42 42' stroke='currentColor' stroke-linecap='round' stroke-linejoin='round'/></svg>
        </button>
      </div>
    `.trim();
  }

  showNotificationPopup(content) {
    const popupWrapper = document.createElement('div');
    popupWrapper.innerHTML = content;
    const popup = popupWrapper.firstElementChild;
    document.body.appendChild(popup);

    popup.querySelector('[data-popup-close]')?.addEventListener('click', () => popup.remove());
  }

  // ---------------------------------------------------------------------------
  // addSizeChartToggleListener()
  // Wires up the size chart drawer toggle button inside the quick view modal.
  // The size chart lives in a theme drawer (#size-chart-*) and is opened by
  // dispatching theme:scroll:lock / theme:scroll:unlock events the theme listens for.
  // ---------------------------------------------------------------------------
  addSizeChartToggleListener() {
    const sizeChartButton = this.querySelector('[data-drawer-toggle][aria-controls^="size-chart"]');
    if (!sizeChartButton) return;

    sizeChartButton.addEventListener('click', (e) => {
      e.preventDefault();

      const drawerId = sizeChartButton.getAttribute('aria-controls');
      const drawer = document.getElementById(drawerId);
      if (!drawer) return;

      drawer.classList.add('alt', 'is-open');
      drawer.setAttribute('aria-hidden', 'false');
      drawer.setAttribute('tabindex', '-1');
      sizeChartButton.setAttribute('aria-expanded', 'true');

      document.dispatchEvent(
        new CustomEvent('theme:scroll:lock', {
          bubbles: true,
          detail: drawer,
        })
      );

      const closeButton = drawer.querySelector('[data-drawer-toggle]');
      if (closeButton) {
        closeButton.addEventListener('click', (ev) => {
          ev.preventDefault();
          drawer.classList.remove('is-open');
          drawer.setAttribute('aria-hidden', 'true');
          drawer.removeAttribute('tabindex');
          sizeChartButton.setAttribute('aria-expanded', 'false');

          document.dispatchEvent(
            new CustomEvent('theme:scroll:unlock', { bubbles: true })
          );
        });
      }

      if (typeof Drawer === 'function') {
        new Drawer(document);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // markSoldOutVariants(product)
  // Iterates all variant option inputs and adds/removes the `sold-out` class
  // on their <label> elements based on whether any available variant exists
  // with that option value. Runs after every variant change so the UI stays
  // accurate as the user makes selections.
  // ---------------------------------------------------------------------------
  markSoldOutVariants(product) {
    const inputs = this.querySelectorAll('[data-single-option-selector]');
    inputs.forEach((input) => {
      const value = input.value;
      const optionIndex = parseInt(input.getAttribute("data-index").replace("option", "")) - 1;
      const available = product.variants.some(variant => variant.options[optionIndex] === value && variant.available);
      const label = this.querySelector(`label[for="${input.id}"]`);
      if (label) label.classList.toggle("sold-out", !available);
    });
  }

  // ---------------------------------------------------------------------------
  // initSwatches()
  // Finds all colour swatch links (a[data-swatch]) inside the modal and binds
  // click handlers that call open() with the swatch's product handle.
  // This triggers a full product reload — new HTML, new gallery, new form —
  // while keeping the modal overlay open (no close/reopen flash).
  // ---------------------------------------------------------------------------
  initSwatches() {
    const swatches = this.querySelectorAll("a[data-swatch]");
    if (!swatches.length) return;
    swatches.forEach((swatch) => {
      swatch.addEventListener("click", (event) => {
        event.preventDefault();
        event.stopPropagation();
        const selectedHandle = swatch.getAttribute("product-handle");
        this.open(selectedHandle);
      });
    });
  }

  // ---------------------------------------------------------------------------
  // initFlickity(gallery)
  // Initialises the Flickity carousel on the product media slider.
  //
  // Reliability measures:
  //   1. Checks for any Flickity instance the theme may have auto-init'd on the
  //      slider element (themeVendor.Flickity.data) and destroys it first —
  //      prevents double-init errors and conflicting drag handlers.
  //   2. Uses imagesLoaded() to defer init until all <img> elements have
  //      loaded — Flickity measures cell widths on init, so calling it before
  //      images are decoded produces wrong dimensions and stacked slides.
  //   3. Calls flickity.resize() after init to force a re-measure in case the
  //      modal animation is still in progress when init runs.
  //   4. Removes qv-gallery--loading (the spinner overlay) only after the
  //      carousel is ready, so the user never sees raw stacked slides.
  //
  // The double requestAnimationFrame in loadProductContent() before this call
  // ensures the browser has painted the new DOM at least once, giving CSS
  // layout a chance to settle before Flickity measures.
  // ---------------------------------------------------------------------------
  initFlickity(gallery) {
    const slider = this.querySelector("[data-product-single-media-slider]");

    // Reveal the gallery (remove spinner overlay) once carousel is ready
    const reveal = () => {
      if (gallery) gallery.classList.remove('qv-gallery--loading');
    };

    if (!slider) { reveal(); return; }

    // Destroy any instance the theme may have auto-initialized on this element
    if (typeof themeVendor?.Flickity?.data === 'function') {
      const existing = themeVendor.Flickity.data(slider);
      if (existing) { try { existing.destroy(); } catch(e) {} }
    }

    const doInit = () => {
      try {
        this.flickity = new themeVendor.Flickity(slider, {
          draggable: true,
          contain: true,
          wrapAround: true,
          cellAlign: "left",
          pageDots: false,
          prevNextButtons: true,
          adaptiveHeight: false
        });
        this.flickity.resize();
      } catch(e) {
        console.error("Error initializing Flickity:", e);
      }
      reveal();
    };

    // Wait for all images in the slider to load so Flickity measures correct dimensions
    if (typeof imagesLoaded === 'function') {
      imagesLoaded(slider, doInit);
    } else {
      doInit();
    }
  }

  connectedCallback() {
    console.log("QuickViewModal connected to the DOM");
  }

  disconnectedCallback() {
    console.log("QuickViewModal removed from the DOM");
  }
}

customElements.define("quick-view-modal", QuickViewModal);

if (typeof NotificationPopup !== 'undefined') {
  window.NotificationPopup = NotificationPopup;
}
